import { collection, doc, getDoc, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
    normalizeAcquisitionPeriods,
    toFiniteNumber,
    toIsoDateOrNull,
} from '../utils/vacationBalanceNormalize';
import type { VacationBalance } from '../models/vacation.types';

class VacationBalanceServiceClass {
    private collectionName = 'vacationBalances';

    async getByCollaboratorId(collaboratorId: string): Promise<VacationBalance | null> {
        if (!collaboratorId) return null;

        const snap = await getDoc(doc(db, this.collectionName, collaboratorId));
        if (!snap.exists()) return null;

        return this.formatBalance(collaboratorId, snap.data());
    }

    async listAll(): Promise<VacationBalance[]> {
        const snapshot = await getDocs(collection(db, this.collectionName));
        return snapshot.docs.map((snap) => this.formatBalance(snap.id, snap.data()));
    }

    private formatBalance(
        collaboratorId: string,
        data: Record<string, unknown>
    ): VacationBalance {
        return {
            collaboratorId,
            collaboratorEmail: String(data.collaboratorEmail ?? ''),
            eligible: Boolean(data.eligible),
            ineligibilityReason: (data.ineligibilityReason ??
                null) as VacationBalance['ineligibilityReason'],
            admissionDate: toIsoDateOrNull(data.admissionDate),
            entitledFrom: toIsoDateOrNull(data.entitledFrom),
            monthsWorked: toFiniteNumber(data.monthsWorked),
            balanceDays: toFiniteNumber(data.balanceDays),
            expiredDays: toFiniteNumber(data.expiredDays),
            pastLimitDays: toFiniteNumber(data.pastLimitDays),
            availableDays: toFiniteNumber(data.availableDays),
            scheduledDays: toFiniteNumber(data.scheduledDays),
            pendingDays: toFiniteNumber(data.pendingDays, 0),
            pendingSoldDays: toFiniteNumber(data.pendingSoldDays, 0),
            nextDeadline: toIsoDateOrNull(data.nextDeadline),
            nextLegalDeadline: toIsoDateOrNull(data.nextLegalDeadline),
            periods: normalizeAcquisitionPeriods(data.periods),
            calculatedAt: data.calculatedAt as Timestamp,
            legacyTakenDays: toFiniteNumber(data.legacyTakenDays),
            legacyScheduledDays: toFiniteNumber(data.legacyScheduledDays),
            legacyInvalidCount: toFiniteNumber(data.legacyInvalidCount, 0),
            unallocatedDays: toFiniteNumber(data.unallocatedDays),
        };
    }
}

export const vacationBalanceService = new VacationBalanceServiceClass();
